// Libraries
import { VictoryLine } from 'victory';
// Types
import { VictoryLines } from './VictoryLines.types';
// Utils
import { lineVariations } from '@theme/victory';

/**
 * Renders VictoryLine components based on the provided VictoryLines data and optional rating ranges.
 *
 * @function
 * @param {VictoryLines} victoryLines - The data representing multiple lines to be rendered on the chart.
 * @param {RatingRanges} [ratingRanges] - The rating ranges used to determine the colors for the gradient stops.
 * @returns {ReactElement[]} - An array of JSX elements representing the rendered VictoryLines.
 *
 * @example
 * const data = [
 *   { x: 1, y: 10 },
 *   { x: 2, y: 15 },
 *   { x: 3, y: 12 },
 *   //...
 * ];
 *
 * const lines = [
 *   { id: 'line1', data: data, styleKey: ChartLineStyles.light, title: 'Line 1' },
 *   { id: 'line2', data: data, styleKey: ChartLineStyles.light, title: 'Line 2' },
 *   //...
 * ];
 *
 * const ratingRanges = {
 *   'A': [0, 10],
 *   'B': [11, 20],
 *   'C': [21, 30],
 *   //...
 * };
 *
 * const renderedLines = renderVictoryLines(lines, ratingRanges);
 * // Returns an array of JSX elements representing the rendered VictoryLines.
 * Important Note: This implementation relies on the Victory library, which does not
 * support creating this
 * component as a separate module due to potential injection vulnerabilities. Please avoid
 * using this
 * function where possible.
 *
 * For more information, refer to the documentation:
 * https://formidable.com/open-source/victory/docs
 */
export const renderVictoryLines = (victoryLines: VictoryLines) => {
  return victoryLines.map((line, index) => {
    const { id, data, styleKey, title } = line;
    const dataSet = data.map((point) => ({ ...point, title }));

    return <VictoryLine key={id} name={`${id}${index}`} data={dataSet} style={lineVariations[styleKey]} />;
  });
};
